/**
 * LocationManager — wraps the browser Geolocation API
 * Manages watchPosition lifecycle and the user location marker on the map
 */

import L from 'leaflet';
import type { LocationError } from './types.js';

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10_000,
  maximumAge: 30_000,
};

const ERROR_MESSAGES: Record<number, { code: LocationError['code']; userMessage: string }> = {
  1: {
    code: 'PERMISSION_DENIED',
    userMessage: 'Location access denied. Enable it in your browser settings to use this feature.',
  },
  2: {
    code: 'POSITION_UNAVAILABLE',
    userMessage: "Your location couldn't be determined. Check your device's location settings.",
  },
  3: {
    code: 'TIMEOUT',
    userMessage: "Location request timed out. Tap 'Locate me' to try again.",
  },
};

export class LocationManager {
  private readonly map: L.Map;
  private watchId: number | null = null;
  private marker: L.CircleMarker | null = null;
  private pulseMarker: L.Marker | null = null;
  private isFirstFix = true;

  constructor(map: L.Map) {
    this.map = map;
  }

  /**
   * Begin tracking the user's position.
   * @param isAutoRestore - When true, skips flyTo on first fix (page-load restore)
   * @param onError - Called with a typed LocationError on any geolocation failure
   * @param onFirstFix - Called once when the first position is obtained successfully
   */
  start(
    isAutoRestore: boolean,
    onError: (err: LocationError) => void,
    onFirstFix?: () => void
  ): void {
    if (this.watchId !== null) {
      return; // already tracking
    }
    this.isFirstFix = true;

    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.onPosition(position, isAutoRestore, onFirstFix),
      (err) => {
        const mapped = ERROR_MESSAGES[err.code] ?? {
          code: 'POSITION_UNAVAILABLE' as const,
          userMessage:
            "Your location couldn't be determined. Check your device's location settings.",
        };
        onError(mapped);
      },
      GEO_OPTIONS
    );
  }

  /**
   * Stop tracking and remove the user marker from the map.
   */
  stop(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.marker !== null) {
      this.marker.remove();
      this.marker = null;
    }
    if (this.pulseMarker !== null) {
      this.pulseMarker.remove();
      this.pulseMarker = null;
    }
  }

  private onPosition(
    position: GeolocationPosition,
    isAutoRestore: boolean,
    onFirstFix?: () => void
  ): void {
    const { latitude: lat, longitude: lng } = position.coords;
    const latlng: L.LatLngExpression = [lat, lng];

    if (this.marker === null) {
      this.createMarker(latlng);
    } else {
      this.marker.setLatLng(latlng);
      this.pulseMarker?.setLatLng(latlng);
    }

    if (this.isFirstFix) {
      this.isFirstFix = false;
      if (!isAutoRestore) {
        this.map.flyTo(latlng, 14);
      }
      onFirstFix?.();
    }
  }

  private createMarker(latlng: L.LatLngExpression): void {
    // Pulsing ring behind the main marker
    const pulseIcon = L.divIcon({
      className: '',
      html: '<div class="location-marker-pulse"></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    this.pulseMarker = L.marker(latlng, {
      icon: pulseIcon,
      interactive: false,
      zIndexOffset: -1,
    }).addTo(this.map);

    // Main filled circle
    this.marker = L.circleMarker(latlng, {
      radius: 10,
      fillColor: '#6c5ce7',
      fillOpacity: 1,
      color: '#ffffff',
      weight: 3,
      className: 'location-marker',
    });
    this.marker.bindTooltip('Your location', { direction: 'top', offset: [0, -12] });
    this.marker.addTo(this.map);
  }
}
